// ===========================================
// components/BookCard.js
// ===========================================
import React from 'react';
import { Eye, Download } from 'lucide-react';
import { formatFileSize, formatDate, openSafely } from '../utils/formatters';
import config from '../utils/config';
import { api } from '../utils/api';

const BookCard = ({ book, onView, onDownload }) => {
  const API_BASE_URL = config.getApiUrl();
  
  const handleViewClick = async () => {
    // Check if file_url exists
    if (!book.file_url) {
      console.error('No file URL available for book:', book.title);
      alert('File not available for viewing');
      return;
    }
    
    try {
      // Use the new view API endpoint
      const response = await api.viewBook(book.id);
      const viewUrl = `${API_BASE_URL.replace('/api', '')}${response.view_url}`;
      console.log('Opening file URL:', viewUrl);
      openSafely(viewUrl);
      onView(book);
    } catch (error) {
      console.error('View failed:', error);
      // Fallback to direct file access if file_url exists
      if (book.file_url) {
        const fileUrl = book.file_url.startsWith('http') 
          ? book.file_url 
          : `${API_BASE_URL.replace('/api', '')}${book.file_url}`;
        console.log('Fallback to direct URL:', fileUrl);
        openSafely(fileUrl);
        onView(book);
      } else {
        alert('File not available for viewing');
      }
    }
  };

  const handleDownloadClick = async () => {
    try {
      // Use the download API to get the proper download URL and increment counter
      const response = await api.downloadBook(book.id);
      const downloadUrl = `${API_BASE_URL.replace('/api', '')}${response.download_url}`;
      
      console.log('Downloading from URL:', downloadUrl);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${book.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      onDownload(book.id);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
      
      // Fallback to direct file access if file_url exists
      if (book.file_url) {
        const fileUrl = book.file_url.startsWith('http') 
          ? book.file_url 
          : `${API_BASE_URL.replace('/api', '')}${book.file_url}`;
        console.log('Fallback download URL:', fileUrl);
        openSafely(fileUrl);
      }
    }
  };

  return (
    <div className="book-card bg-white rounded-xl shadow-md overflow-hidden fade-in">
      {/* Book Cover */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 group">
        <img
          src={book.thumbnail_url 
            ? (book.thumbnail_url.startsWith('http') 
                ? book.thumbnail_url 
                : `${API_BASE_URL.replace('/api', '')}${book.thumbnail_url}`)
            : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDIwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjNjM2NmYxIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj5Cb29rPC90ZXh0Pgo8L3N2Zz4K'
          }
          alt={book.title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          crossOrigin="anonymous"
          onError={(e) => {
            console.log('Image failed to load:', book.thumbnail_url);
            // Set to a simple colored div instead of external placeholder
            e.target.style.display = 'none';
            e.target.parentElement.style.backgroundColor = '#6366f1';
            e.target.parentElement.innerHTML = '<div class="w-full h-48 bg-indigo-500 flex items-center justify-center"><span class="text-white text-lg font-medium">📚 Book</span></div>';
          }}
        />
        
        {/* Overlay with actions */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex gap-2">
            <button
              onClick={handleViewClick}
              className="bg-white text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-colors transform scale-90 hover:scale-100"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={handleDownloadClick}
              className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors transform scale-90 hover:scale-100"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Download count badge */}
        {book.download_count > 0 && (
          <div className="absolute top-2 right-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded-full">
              <Download className="h-3 w-3" />
              {book.download_count}
            </span>
          </div>
        )}
      </div>

      {/* Book Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1 text-sm line-clamp-2">
          {book.title}
        </h3>
        <p className="text-gray-600 text-xs mb-2">{book.author || 'Unknown Author'}</p>
        
        {/* Metadata */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
          <span>{formatFileSize(book.file_size)}</span>
          <span>{formatDate(book.upload_date)}</span>
        </div>
        
        {/* Uploaded by */}
        <p className="text-gray-500 text-xs mb-3">
          by {book.uploaded_by}
        </p>
        
        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleViewClick}
            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
          >
            <Eye className="h-3 w-3" />
            View
          </button>
          <button
            onClick={handleDownloadClick}
            className="flex-1 bg-gray-600 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-gray-700 transition-colors flex items-center justify-center gap-1"
          >
            <Download className="h-3 w-3" />
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookCard;